import { FormEvent, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { styled } from '@mui/material/styles';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';

import house from '../assets/norma_login.jpg';
import logo from '../assets/modsy_logo_with_wordmark.png';
import logoDark from '../assets/modsy_logo_with_wordmark_dark_mode.png';

import { login } from '../libraries/auth';

const StyledTitle = styled(Typography)<{component: string;}>`
	font-size: 40px;
	font-style: normal;
	font-weight: 500;
	line-height: 1;
	text-align: center;
	letter-spacing: 0.15px;
	color: ${(props) => props.theme.palette.text.primary};
	margin-bottom: 26px;
`;

const StyledText = styled(Typography)<{component: string;}>`
	margin-bottom: 80px;
	font-style: normal;
	font-weight: 400;
	font-size: 18px;
	line-height: 24px;
	text-align: center;
	letter-spacing: 0.15px;
	color: ${(props) => props.theme.palette.text.primary};
`;

export default function Login({
	env,
	companySlug,
	title,
	description,
	navigateTo,
}: {
	env: 'dev' | 'staging' | 'prod';
	companySlug: string;
	title: string;
	description: string;
	navigateTo: string;
})
{
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();

	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [error, setError] = useState('');

	const onSubmit = async (event: FormEvent) =>
	{
		event.preventDefault();
		try
		{
			await login(env, companySlug, email, password);
			navigate(searchParams.get('next') || navigateTo, { replace: true });
		} catch (err)
		{
			setError(String(err));
		}
	};

	const isUsingDarkTheme = window['theme'] ? window['theme']['palette']['mode'] === 'dark' : false;
	const logoImage = isUsingDarkTheme ? logoDark : logo;

	return (
		<Grid container spacing={0}>
			<Grid item xs={6}>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'center',
						alignItems: `center`,
						height: '100vh',
						width: '100%',
						maxWidth: '400px',
						margin: '0 auto',
						flexDirection: 'column',
					}}
				>
					<Box
						component="img"
						src={logoImage}
						alt="Logo"
						id="ModsyLogo"
						sx={{
							width: '100%',
							maxWidth: '140px',
							marginBottom: '120px',
						}}
					/>
					<StyledTitle component="h1">
						{title}
					</StyledTitle>
					<StyledText component="p">
						{description}
					</StyledText>

					<form onSubmit={onSubmit} noValidate>
						<TextField
							InputLabelProps={{ shrink: true }}
							autoComplete="email"
							fullWidth
							id="email"
							label="Email"
							type="email"
							value={email}
							sx={{ marginBottom: '30px' }}
							onChange={(event) => setEmail(event.target.value)}
						/>
						<TextField
							InputLabelProps={{ shrink: true }}
							autoComplete="current-password"
							fullWidth
							id="password"
							label="Password"
							type="password"
							value={password}
							sx={{ marginBottom: '30px' }}
							onChange={(event) => setPassword(event.target.value)}
						/>

						{error && (
							<Typography
								sx={{
									textAlign: 'center',
									marginBottom: '30px',
									color: 'rgb(232, 55, 28)',
								}}
							>
								{error}
							</Typography>
						)}

						<Button
							size="large"
							type="submit"
							variant="contained"
							sx={{
								width: '100%',
							}}
						>
							SIGN IN
						</Button>
					</form>
				</Box>
			</Grid>

			<Grid item xs={6}>
				<Box
					sx={{
						backgroundImage: `url(${house})`,
						height: '100vh',
						backgroundPosition: `center`,
						backgroundRepeat: `no-repeat`,
						backgroundSize: `cover`,
					}}
				/>
			</Grid>
		</Grid>
	);
}
